import type { QueryClient, QueryKey } from "@tanstack/react-query";
import type { RealtimeEnvelope, WorkshopLiveFrame } from "@/types/realtime.type";
import type { WorkshopSession } from "@/types/workshop.type";
import type { RealtimeHandler } from "../../api/realtimeHandlers";
import {
  WORKSHOP_PROJECTS_ROOT,
  workshopArtifactKey,
  workshopArtifactNotesRootKey,
  workshopArtifactRevisionsKey,
  workshopArtifactsKey,
  workshopContextKey,
  workshopDeploymentMessagesKey,
  workshopDeploymentRunsKey,
  workshopDeploymentsKey,
  workshopFilesKey,
  workshopIdeaKey,
  workshopIdeasKey,
  workshopMediaKey,
  workshopMembersKey,
  workshopProjectKey,
  workshopProjectsKey,
  workshopSessionsKey,
} from "./workshopKeys";

/**
 * Workshop collaboration over the socket. Hints (realtime:event) say what a
 * collaborator changed; the matching reads refetch. The project list and the
 * thread lists hear about other people's presence from `presence` frames.
 */

const invalidate = (qc: QueryClient, queryKey: QueryKey) =>
  void qc.invalidateQueries({ queryKey });

export const WORKSHOP_REALTIME: RealtimeHandler = {
  apply(qc, event: RealtimeEnvelope) {
    switch (event.type) {
      case "workshop.artifacts.changed": {
        const { projectId, ids } = event.data;
        invalidate(qc, workshopArtifactsKey(projectId));
        for (const id of ids) {
          invalidate(qc, workshopArtifactKey(id));
          invalidate(qc, workshopArtifactRevisionsKey(id));
        }
        invalidate(qc, WORKSHOP_PROJECTS_ROOT);
        return;
      }
      case "workshop.notes.changed":
        invalidate(qc, workshopArtifactNotesRootKey(event.data.projectId));
        // The open-note counts on the artifact rows.
        invalidate(qc, workshopArtifactsKey(event.data.projectId));
        return;
      case "workshop.ideas.changed": {
        const { projectId, ids } = event.data;
        invalidate(qc, workshopIdeasKey(projectId));
        for (const id of ids) invalidate(qc, workshopIdeaKey(id));
        return;
      }
      case "workshop.context.changed":
        invalidate(qc, workshopContextKey(event.data.projectId));
        return;
      case "workshop.media.changed":
        invalidate(qc, workshopMediaKey(event.data.projectId));
        return;
      case "workshop.files.changed":
        // The prefix covers the Sources list and every thread's strip.
        invalidate(qc, workshopFilesKey(event.data.projectId));
        return;
      case "workshop.deployments.changed": {
        const { projectId, ids } = event.data;
        invalidate(qc, workshopDeploymentsKey(projectId));
        for (const id of ids) {
          invalidate(qc, workshopDeploymentRunsKey(id));
          invalidate(qc, workshopDeploymentMessagesKey(id));
        }
        invalidate(qc, WORKSHOP_PROJECTS_ROOT);
        return;
      }
      case "workshop.project.updated":
      case "workshop.project.deleted":
        invalidate(qc, workshopProjectKey(event.data.projectId));
        invalidate(qc, WORKSHOP_PROJECTS_ROOT);
        return;
      case "workshop.members.changed":
        invalidate(qc, workshopProjectKey(event.data.projectId));
        invalidate(qc, workshopMembersKey(event.data.projectId));
        invalidate(qc, WORKSHOP_PROJECTS_ROOT);
        return;
      case "workshop.threads.changed":
        invalidate(qc, workshopSessionsKey(event.data.projectId));
        invalidate(qc, WORKSHOP_PROJECTS_ROOT);
        return;
      case "workshop.thread.moved":
        // It left one project's chats and joined the other's.
        invalidate(qc, workshopSessionsKey(event.data.fromProjectId));
        invalidate(qc, workshopSessionsKey(event.data.toProjectId));
        invalidate(qc, WORKSHOP_PROJECTS_ROOT);
        return;
      default:
        return;
    }
  },
  resyncKeys: (brandId) => [workshopProjectsKey(brandId)],
};

/** What a project room may have missed while this tab was not in it. */
export function workshopProjectResyncKeys(projectId: string): QueryKey[] {
  return [
    workshopProjectKey(projectId),
    workshopMembersKey(projectId),
    workshopSessionsKey(projectId),
    workshopArtifactsKey(projectId),
    workshopArtifactNotesRootKey(projectId),
    workshopIdeasKey(projectId),
    workshopContextKey(projectId),
    workshopMediaKey(projectId),
    workshopFilesKey(projectId),
    workshopDeploymentsKey(projectId),
  ];
}

/** Each thread's last announced people, so only a real change refetches the project cards. */
const lastPresence = new Map<string, string>();

/**
 * Presence reaches everyone in a thread wherever they are. The thread's row
 * in its project's thread list is patched in place; the project cards'
 * collaborator marks are a union across threads, so the list refetches, but
 * only when who is there (or away) changed: a run starting or ending also
 * announces presence, with the same people.
 */
export function applyWorkshopLiveFrame(qc: QueryClient, frame: WorkshopLiveFrame) {
  if (frame.type !== "presence") return;
  qc.setQueryData<WorkshopSession[]>(workshopSessionsKey(frame.projectId), (list) =>
    list?.map((session) =>
      session.id === frame.sessionId ? { ...session, present: frame.users } : session,
    ),
  );
  const people = frame.users
    .map((u) => `${u.userId}:${u.state}`)
    .sort()
    .join(",");
  if (lastPresence.get(frame.sessionId) === people) return;
  lastPresence.set(frame.sessionId, people);
  invalidate(qc, WORKSHOP_PROJECTS_ROOT);
}
